import api from './api';
import type { ContractDetails, StudentContract } from '../types/studentPortal';
import type { PublicQuotePreview } from './publicContract.service';

/** The partner page (/samir-contract). Every call carries the partner access code. */

export interface PartnerOffer {
  key: string;
  title: string;
  title_ar: string;
  /** The language track the offer includes; its question is not asked. */
  language_status?: string | null;
}

export interface PartnerDestination {
  country_id: number;
  name: string;
  code: string;
  offers: PartnerOffer[];
}

export interface PartnerStudentRequest extends ContractDetails {
  email: string;
  first_name: string;
  last_name: string;
  country_id: number;
  answers: Record<string, string>;
  special_offer?: string | null;
}

export interface PartnerStudentCreated {
  user_id: number;
  name: string;
  email: string;
  password: string;
  application_id: number;
  application_number: string;
  currency: string;
  payable_total: string;
  deposit_amount: string;
  package_title_ar: string;
  email_queued: boolean;
  contract: StudentContract;
}

const headers = (code: string) => ({ 'X-Partner-Code': code });

class PartnerContractService {
  async openSession(code: string): Promise<void> {
    await api.post('/partner/session', { code });
  }

  async destinations(code: string): Promise<PartnerDestination[]> {
    const { data } = await api.get<PartnerDestination[]>('/partner/destinations', { headers: headers(code) });
    return data;
  }

  async quote(code: string, countryId: number, answers: Record<string, string>, specialOffer: string | null): Promise<PublicQuotePreview> {
    const { data } = await api.post<PublicQuotePreview>(
      '/partner/quote',
      { country_id: countryId, answers, special_offer: specialOffer },
      { headers: headers(code) },
    );
    return data;
  }

  async register(code: string, payload: PartnerStudentRequest): Promise<PartnerStudentCreated> {
    const { data } = await api.post<PartnerStudentCreated>('/partner/students', payload, { headers: headers(code) });
    return data;
  }
}

export default new PartnerContractService();
